import { Injectable } from '@angular/core';
import { BehaviorSubject, map, merge, reduce, Subscription, tap } from 'rxjs';

import { AbstractCachePagingService, ChannelUtil, ContactEntryDoc, JsonMap, MediaType, PagingParams } from '@contact/upgrade';

import { HistoryService } from '../history.service';

export enum HistoryInteractionFilter {
  FIRSTNAME = 'FIRSTNAME',
  LASTNAME = 'LASTNAME',
  COMPANY_NAME = 'COMPANY_NAME',
  URI = 'URI',
  CHANNEL = 'CHANNEL',
  CAMPAIGN = 'CAMPAIGN',
  CSR = 'CSR',
  BEGIN_DATE = 'BEGIN_DATE',
  END_DATE = 'END_DATE',
}

export type RecordStatusCache = { [key: string]: string };

@Injectable({
  providedIn: 'root',
})
export class HistoryInteractionSource extends AbstractCachePagingService<ContactEntryDoc> {
  get recordStatusCache$() {
    return this._recordStatusCache$.asObservable();
  }
  private _recordStatusCache$ = new BehaviorSubject<RecordStatusCache>({});

  private sub = Subscription.EMPTY;

  constructor(private readonly historyService: HistoryService) {
    super();
  }

  protected _load(param: PagingParams<JsonMap>) {
    this.sub.unsubscribe();
    // extract filter
    const form = param && param.form;
    const firstname = form && (form[HistoryInteractionFilter.FIRSTNAME] as string);
    const lastname = form && (form[HistoryInteractionFilter.LASTNAME] as string);
    const companyname = form && (form[HistoryInteractionFilter.COMPANY_NAME] as string);
    const uri = form && (form[HistoryInteractionFilter.URI] as string);
    const channelStr = form && (form[HistoryInteractionFilter.CHANNEL] as string);
    const channel = channelStr ? ChannelUtil.from(channelStr) : undefined;
    const campaign = form && (form[HistoryInteractionFilter.CAMPAIGN] as string);
    const csr = form && (form[HistoryInteractionFilter.CSR] as string);
    const beginDate = form && (form[HistoryInteractionFilter.BEGIN_DATE] as string);
    let endDate = form && (form[HistoryInteractionFilter.END_DATE] as string);
    if (endDate !== undefined) {
      const endDateString = endDate || '';
      if (endDateString !== '') {
        const endDayTime = new Date(endDateString);
        endDayTime.setHours(23, 59, 59, 999);
        endDate = endDayTime.toISOString();
      }
    }
    const index = (param && param.pageIndex) || 0;
    const size = (param && param.pageSize) || 10;
    const from = index * (size || 0);
    let sort = param && param.sortActive;
    let direction: '' | 'asc' | 'desc' = (param && param.sortDirection) || '';
    if (sort && sort.toLowerCase().indexOf('date') === -1) {
      sort = `${sort}.keyword`;
    }
    // add a default sort
    if (!sort) {
      sort = 'entryDate';
      direction = 'desc';
    }
    return this.historyService
      .search({
        firstname,
        lastname,
        companyname,
        uri,
        channel,
        campaign,
        csr,
        beginDate,
        endDate,
        size,
        from,
        sort: sort && direction ? { [sort]: direction } : undefined,
      })
      .pipe(
        map(result => ({
          ...result,
          ...param,
        })),
        // retrieve record status for phone interaction
        tap(({ data }) => {
          this.sub = merge(
            ...data
              .filter(({ canalName }) => canalName === MediaType.PHONE)
              .filter(({ technicalId }) => !!technicalId)
              .map(({ uuid, technicalId }) =>
                this.historyService.getRecordStatus(technicalId).pipe(
                  map(status => ({
                    key: uuid,
                    value: status,
                  }))
                )
              )
          )
            .pipe(
              reduce(
                (acc, cur) => ({
                  ...acc,
                  [cur.key]: cur.value ?? 'UNKNOWN',
                }),
                {} as RecordStatusCache
              )
            )
            .subscribe(result => this._recordStatusCache$.next(result));
        })
      );
  }

  override reset() {
    this.sub.unsubscribe();
    this._recordStatusCache$.next({});
    super.reset();
  }

  override ngOnDestroy() {
    this.sub.unsubscribe();
    this._recordStatusCache$.complete();
    super.ngOnDestroy();
  }
}
